/*
 * Per-value freshness classifier (concept B.5 — Datenfrische ist sicherheits-
 * kritisch).
 *
 * Rules, in evaluation order:
 *   - 'override'      → quality Override (Force aktiv, D.5) — own warning display.
 *   - 'bad'           → quality Bad.
 *   - 'disconnected'  → hub is currently disconnected or reconnecting.
 *   - 'live'          → received within last 10 s AND quality Good.
 *   - 'stale'         → received within last 60 s, quality Good or Uncertain.
 *   - 'stale'         → older than 60 s, but still last known value.
 *
 * Tones map to token colours consumed by the tree; the caller pulls the token
 * name (`--status-ok` etc.) itself, we only classify.
 */

import type { ConnectionState } from './hubClient';
import type { LeafValue } from './signalStore';

export type FreshnessLabel = 'live' | 'stale' | 'disconnected' | 'bad' | 'override';
export type FreshnessTone = 'ok' | 'warn' | 'error' | 'info';

export interface FreshnessResult {
  label: FreshnessLabel;
  tone: FreshnessTone;
}

const LIVE_WINDOW_MS = 10_000;
const STALE_WINDOW_MS = 60_000;

/** Classify a leaf value against the current hub state.
 *
 *  `value` may be null when no update has arrived yet — in that case we treat
 *  the point as `disconnected` if the hub isn't connected, else `stale`
 *  (waiting for first sample). Callers typically render '—' anyway. */
export function classifyFreshness(
  value: LeafValue | null,
  connection: ConnectionState,
  now: number = Date.now(),
  /** Schwelle je Datenpunkt (hmi/staleness.ts, staleAfterMsFor). Ohne sie
   *  gilt das alte Fenster von 10 s; das ist fuer Werte, die nur bei
   *  Aenderung kommen, viel zu kurz (Patrick, 16.10.2026: nach dem
   *  Tabwechsel war plötzlich alles "Stale"). */
  staleAfterMs?: number,
): FreshnessResult {
  if (!value) {
    if (connection !== 'connected') {
      return { label: 'disconnected', tone: 'error' };
    }
    return { label: 'stale', tone: 'warn' };
  }

  if (value.q === 'Override') return { label: 'override', tone: 'info' };
  if (value.q === 'Bad') return { label: 'bad', tone: 'error' };

  if (connection === 'disconnected' || connection === 'reconnecting') {
    return { label: 'disconnected', tone: 'error' };
  }

  // Massgeblich ist der Zeitpunkt des Werts, nicht der Empfang im Browser:
  // ein Wert aus dem Abruf beim Oeffnen der Seite ist so alt wie gemessen.
  const at = Number.isFinite(Date.parse(value.t)) ? Math.min(Date.parse(value.t), value.receivedAt) : value.receivedAt;
  const age = now - (staleAfterMs !== undefined ? at : value.receivedAt);
  if (age <= (staleAfterMs ?? LIVE_WINDOW_MS) && value.q === 'Good') {
    return { label: 'live', tone: 'ok' };
  }
  if (age <= STALE_WINDOW_MS && (value.q === 'Good' || value.q === 'Uncertain')) {
    return { label: 'stale', tone: 'warn' };
  }
  return { label: 'stale', tone: 'warn' };
}

/** German UI labels — matches the small legend at the top of the plant page.
 *  Kept in the module so the same wording is used wherever we render the dot. */
export function freshnessLabelDe(label: FreshnessLabel): string {
  switch (label) {
    case 'live': return 'Live';
    case 'stale': return 'Stale';
    case 'disconnected': return 'Getrennt';
    case 'bad': return 'Ungültig';
    case 'override': return 'Override';
  }
}
